import React, { useState, useEffect, useCallback } from 'react';
import { Contact } from '../../../../config/api';
import { useKeycloak } from '@react-keycloak/web';
import ClientMattersWidget from '../ClientMattersWidget';
import type { ClientBillingStats } from '../../hooks/useClientBilling';

// ─── Types ──────────────────────────────────────────────────────

interface FormData {
  name: string;
  email: string;
  phone: string;
  address: string;
  status: string;
  contact_type: string;
  tags: string[];
  notes: string;
  company: string;
  title: string;
  dateOfBirth: string;
}

interface BillingData {
  stats: ClientBillingStats;
  loading: boolean;
  error: string | null;
}

interface ActivityItem {
  id: string;
  type: 'bill_created' | 'payment_received' | 'time_entry' | 'expense' | 'document_uploaded' | 'matter_opened' | 'communication';
  title: string;
  description: string;
  amount?: number;
  matter_title?: string;
  matter_id?: string;
  created_at: string;
}

interface ContractRecord {
  id: string;
  name: string;
  matter_title?: string;
  matter_id?: string;
  status: 'signed' | 'pending' | 'expired' | 'draft';
  type: 'engagement_letter' | 'fee_agreement' | 'retainer' | 'nda' | 'other';
  signed_date?: string;
  expiry_date?: string;
}

interface OverviewTabProps {
  contact: Contact;
  billing?: BillingData;
  isEditing?: boolean;
  formData?: FormData;
  setFormData?: (fn: (prev: FormData) => FormData) => void;
}

// ─── Helpers ────────────────────────────────────────────────────

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function relativeTime(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateStr);
}

const ACTIVITY_ICONS: Record<ActivityItem['type'], { icon: string; color: string }> = {
  bill_created: { icon: '💰', color: 'bg-amber-100 dark:bg-amber-900/20' },
  payment_received: { icon: '✅', color: 'bg-green-100 dark:bg-green-900/20' },
  time_entry: { icon: '⏱', color: 'bg-blue-100 dark:bg-blue-900/20' },
  expense: { icon: '🧾', color: 'bg-orange-100 dark:bg-orange-900/20' },
  document_uploaded: { icon: '📄', color: 'bg-purple-100 dark:bg-purple-900/20' },
  matter_opened: { icon: '📂', color: 'bg-indigo-100 dark:bg-indigo-900/20' },
  communication: { icon: '💬', color: 'bg-cyan-100 dark:bg-cyan-900/20' },
};

const CONTRACT_STATUS_COLORS: Record<ContractRecord['status'], string> = {
  signed: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200',
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200',
  expired: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200',
  draft: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-200',
};

function getAuthToken(): string | null {
  return (window as any).keycloak?.token
    || sessionStorage.getItem('token')
    || localStorage.getItem('access_token')
    || null;
}

// ─── Contact Details Card ───────────────────────────────────────

const ContactDetailsCard: React.FC<{
  contact: Contact;
  isEditing: boolean;
  formData?: FormData;
  setFormData?: (fn: (prev: FormData) => FormData) => void;
}> = ({ contact, isEditing, formData, setFormData }) => {

  const editField = (field: keyof FormData, value: string) => {
    if (setFormData) {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const fields = [
    { label: 'Email', value: isEditing ? formData?.email : contact.email, field: 'email' as const, type: 'email' },
    { label: 'Phone', value: isEditing ? formData?.phone : contact.phone, field: 'phone' as const, type: 'tel' },
    { label: 'Company', value: isEditing ? formData?.company : (contact.custom_fields?.company as string), field: 'company' as const, type: 'text' },
    { label: 'Title', value: isEditing ? formData?.title : (contact.custom_fields?.title as string), field: 'title' as const, type: 'text' },
    { label: 'Date of Birth', value: isEditing ? formData?.dateOfBirth : (contact.custom_fields?.dateOfBirth as string), field: 'dateOfBirth' as const, type: 'date' },
    { label: 'Address', value: isEditing ? formData?.address : contact.address, field: 'address' as const, type: 'text' },
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Contact Details</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {fields.map(({ label, value, field, type }) => (
          <div key={field}>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">{label}</label>
            {isEditing ? (
              <input
                type={type}
                value={value || ''}
                onChange={(e) => editField(field, e.target.value)}
                className="w-full text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md px-2.5 py-1.5 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-lex-accent"
                placeholder={`Enter ${label.toLowerCase()}`}
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white break-words">
                {value || <span className="text-gray-400 italic">Not set</span>}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Tags */}
      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Tags</label>
        {contact.tags && contact.tags.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {contact.tags.map((tag, i) => (
              <span key={i} className="px-2 py-0.5 bg-lex-accent-soft text-lex-accent rounded-full text-xs">
                {tag}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 italic">No tags</p>
        )}
      </div>

      {/* Dates */}
      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 flex gap-6 text-xs text-gray-500 dark:text-gray-400">
        <span>Created: {formatDate(contact.created_at)}</span>
        <span>Updated: {formatDate(contact.updated_at)}</span>
        {contact.last_contact_date && <span>Last Contact: {formatDate(contact.last_contact_date)}</span>}
      </div>
    </div>
  );
};

// ─── Financial Health Summary ───────────────────────────────────

const FinancialHealthCard: React.FC<{ stats: ClientBillingStats }> = ({ stats }) => {
  const arTotal = stats.arCurrent + stats.ar31to60 + stats.ar61to90 + stats.ar91Plus;
  const billedVsCollected = stats.billsTotal > 0
    ? ((stats.billsPaid / stats.billsTotal) * 100).toFixed(0)
    : '0';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Financial Health</h4>

      {/* P&L Summary Row */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-lex-accent-soft rounded-lg px-3 py-2">
          <div className="text-[10px] uppercase font-medium text-lex-accent">Total Billed</div>
          <div className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">{formatCurrency(stats.billsTotal)}</div>
        </div>
        <div className="bg-green-50 dark:bg-green-900/15 rounded-lg px-3 py-2">
          <div className="text-[10px] uppercase font-medium text-green-600 dark:text-green-400">Collected</div>
          <div className="text-lg font-bold text-green-900 dark:text-green-100 tabular-nums">{formatCurrency(stats.billsPaid)}</div>
          <div className="text-[10px] text-green-600 dark:text-green-400">{billedVsCollected}% rate</div>
        </div>
        <div className="bg-red-50 dark:bg-red-900/15 rounded-lg px-3 py-2">
          <div className="text-[10px] uppercase font-medium text-red-600 dark:text-red-400">Outstanding</div>
          <div className="text-lg font-bold text-red-900 dark:text-red-100 tabular-nums">{formatCurrency(stats.billsOutstanding)}</div>
        </div>
      </div>

      {/* AR Aging Mini-Bar */}
      {arTotal > 0 && (
        <div className="mb-4">
          <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">AR Aging</div>
          <div className="flex rounded-md overflow-hidden h-4">
            {stats.arCurrent > 0 && (
              <div
                className="bg-green-400 dark:bg-green-600 flex items-center justify-center text-[9px] text-white font-medium"
                style={{ width: `${Math.max((stats.arCurrent / arTotal) * 100, 8)}%` }}
                title={`Current: ${formatCurrency(stats.arCurrent)}`}
              />
            )}
            {stats.ar31to60 > 0 && (
              <div
                className="bg-yellow-400 dark:bg-yellow-600 flex items-center justify-center text-[9px] text-white font-medium"
                style={{ width: `${Math.max((stats.ar31to60 / arTotal) * 100, 8)}%` }}
                title={`31-60 days: ${formatCurrency(stats.ar31to60)}`}
              />
            )}
            {stats.ar61to90 > 0 && (
              <div
                className="bg-orange-400 dark:bg-orange-600 flex items-center justify-center text-[9px] text-white font-medium"
                style={{ width: `${Math.max((stats.ar61to90 / arTotal) * 100, 8)}%` }}
                title={`61-90 days: ${formatCurrency(stats.ar61to90)}`}
              />
            )}
            {stats.ar91Plus > 0 && (
              <div
                className="bg-red-400 dark:bg-red-600 flex items-center justify-center text-[9px] text-white font-medium"
                style={{ width: `${Math.max((stats.ar91Plus / arTotal) * 100, 8)}%` }}
                title={`90+ days: ${formatCurrency(stats.ar91Plus)}`}
              />
            )}
          </div>
          <div className="flex gap-3 mt-1 text-[10px] text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" /> Current</span>
            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-yellow-400 inline-block" /> 31-60d</span>
            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-orange-400 inline-block" /> 61-90d</span>
            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block" /> 90d+</span>
          </div>
        </div>
      )}

      {/* Trust + WIP Row */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-teal-50 dark:bg-teal-900/15 rounded-lg px-3 py-2">
          <div className="text-[10px] uppercase font-medium text-teal-600 dark:text-teal-400">Trust Balance</div>
          <div className="text-base font-bold text-teal-900 dark:text-teal-100 tabular-nums">{formatCurrency(stats.trustBalance)}</div>
          <div className="text-[10px] text-teal-600 dark:text-teal-400">{formatCurrency(stats.trustTotalDeposits)} deposited</div>
        </div>
        <div className="bg-amber-50 dark:bg-amber-900/15 rounded-lg px-3 py-2">
          <div className="text-[10px] uppercase font-medium text-amber-600 dark:text-amber-400">Work in Progress</div>
          <div className="text-base font-bold text-amber-900 dark:text-amber-100 tabular-nums">{formatCurrency(stats.totalWip)}</div>
          <div className="text-[10px] text-amber-600 dark:text-amber-400">{stats.billableHours.toFixed(1)} billable hrs</div>
        </div>
      </div>
    </div>
  );
};

// ─── Contracts Section ──────────────────────────────────────────

const ContractsSection: React.FC<{ clientId: string }> = ({ clientId }) => {
  const [contracts, setContracts] = useState<ContractRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContracts = async () => {
      setLoading(true);
      try {
        const token = getAuthToken();
        if (!token) { setLoading(false); return; }

        // Fetch documents categorized as contracts for this client
        const res = await fetch(`/api/v1/dms/documents?client_id=${clientId}&category=contracts`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });

        if (res.ok) {
          const data = await res.json();
          const docs = data.data || data.documents || [];
          // Map DMS documents to contract records
          const mapped: ContractRecord[] = docs.map((doc: any) => {
            const name = doc.name || doc.filename || 'Untitled';
            const lowerName = name.toLowerCase();
            let type: ContractRecord['type'] = 'other';
            if (lowerName.includes('engagement')) type = 'engagement_letter';
            else if (lowerName.includes('fee') || lowerName.includes('rate')) type = 'fee_agreement';
            else if (lowerName.includes('retainer')) type = 'retainer';
            else if (lowerName.includes('nda') || lowerName.includes('confidential')) type = 'nda';

            return {
              id: doc.id || doc.document_id,
              name,
              matter_title: doc.matter?.title || doc.matter?.description,
              matter_id: doc.matter_id,
              status: doc.metadata?.contract_status || (doc.metadata?.signed ? 'signed' : 'draft'),
              type,
              signed_date: doc.metadata?.signed_date,
              expiry_date: doc.metadata?.expiry_date,
            };
          });
          setContracts(mapped);
        }
      } catch {
        // Contracts are optional — silently handle errors
      } finally {
        setLoading(false);
      }
    };

    fetchContracts();
  }, [clientId]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Contracts & Agreements</h4>
        <div className="animate-pulse space-y-2">
          <div className="h-10 bg-gray-100 dark:bg-gray-700 rounded" />
          <div className="h-10 bg-gray-100 dark:bg-gray-700 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Contracts & Agreements</h4>

      {contracts.length === 0 ? (
        <div className="text-center py-6">
          <svg className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
          </svg>
          <p className="text-xs text-gray-500 dark:text-gray-400">No contracts found</p>
          <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
            Upload engagement letters or fee agreements to the Documents tab with "Contracts" category
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {contracts.map((contract) => (
            <div key={contract.id} className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{contract.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] text-gray-500 dark:text-gray-400 capitalize">
                    {contract.type.replace(/_/g, ' ')}
                  </span>
                  {contract.matter_title && (
                    <span className="text-[10px] text-lex-accent dark:text-white truncate">{contract.matter_title}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                {contract.expiry_date && (
                  <span className="text-[10px] text-gray-400">{formatDate(contract.expiry_date)}</span>
                )}
                <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${CONTRACT_STATUS_COLORS[contract.status]}`}>
                  {contract.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Cross-Matter Activity Timeline ─────────────────────────────

const ActivityTimeline: React.FC<{ clientId: string }> = ({ clientId }) => {
  const { keycloak } = useKeycloak();
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActivity = async () => {
      setLoading(true);
      try {
        const token = keycloak?.token || getAuthToken();
        if (!token) { setLoading(false); return; }

        const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
        const params = new URLSearchParams({ client_id: clientId, limit: '20' });

        // Fetch recent billing activity + communications in parallel
        const [billsRes, paymentsRes, commsRes] = await Promise.all([
          fetch(`/api/v1/billing/bills?${params}&sort=created_at&order=desc`, { headers }).catch(() => null),
          fetch(`/api/v1/billing/payments?${params}&sort=created_at&order=desc`, { headers }).catch(() => null),
          fetch(`/api/v1/contacts/${clientId}/communications?page=1&limit=10`, { headers }).catch(() => null),
        ]);

        const items: ActivityItem[] = [];

        // Bills
        if (billsRes?.ok) {
          const data = await billsRes.json();
          const bills = data.data || [];
          bills.slice(0, 5).forEach((b: any) => {
            items.push({
              id: `bill-${b.bill_id}`,
              type: 'bill_created',
              title: `Bill ${b.bill_number || ''} created`,
              description: b.description || `${formatCurrency(Number(b.total))} billed`,
              amount: Number(b.total),
              matter_title: b.matter?.description || b.matter?.matter_number,
              matter_id: b.matter_id,
              created_at: b.created_at || b.issue_date,
            });
          });
        }

        // Payments
        if (paymentsRes?.ok) {
          const data = await paymentsRes.json();
          const payments = data.data?.payments || data.data || [];
          payments.slice(0, 5).forEach((p: any) => {
            items.push({
              id: `payment-${p.payment_id || p.id}`,
              type: 'payment_received',
              title: `Payment received`,
              description: `${formatCurrency(Number(p.amount))} via ${p.payment_method || 'check'}`,
              amount: Number(p.amount),
              matter_title: p.matter?.description,
              matter_id: p.matter_id,
              created_at: p.payment_date || p.created_at,
            });
          });
        }

        // Communications
        if (commsRes?.ok) {
          const data = await commsRes.json();
          const comms = data.communications || [];
          comms.slice(0, 5).forEach((c: any) => {
            items.push({
              id: `comm-${c.id}`,
              type: 'communication',
              title: c.subject || `${c.type} ${c.direction}`,
              description: c.preview || '',
              created_at: c.date || c.created_at,
            });
          });
        }

        // Sort by date descending
        items.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        setActivities(items.slice(0, 15));
      } catch {
        // Activity is best-effort
      } finally {
        setLoading(false);
      }
    };

    fetchActivity();
  }, [clientId, keycloak?.token]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Recent Activity</h4>
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
                <div className="h-2.5 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Recent Activity</h4>

      {activities.length === 0 ? (
        <div className="text-center py-6">
          <svg className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
          </svg>
          <p className="text-xs text-gray-500 dark:text-gray-400">No recent activity</p>
        </div>
      ) : (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 top-4 bottom-4 w-px bg-gray-200 dark:bg-gray-700" />

          <div className="space-y-3">
            {activities.map((item) => {
              const meta = ACTIVITY_ICONS[item.type];
              return (
                <div key={item.id} className="flex items-start gap-3 relative">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full ${meta.color} flex items-center justify-center text-sm z-10`}>
                    {meta.icon}
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{item.title}</p>
                      <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0 tabular-nums">{relativeTime(item.created_at)}</span>
                    </div>
                    {item.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{item.description}</p>
                    )}
                    {item.matter_title && (
                      <span className="text-[10px] text-lex-accent dark:text-white">{item.matter_title}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main OverviewTab ───────────────────────────────────────────

const OverviewTab: React.FC<OverviewTabProps> = ({ contact, billing, isEditing = false, formData, setFormData }) => {
  const stats = billing?.stats;

  return (
    <div className="space-y-6">
      {/* Row 1: Contact Details + Financial Health */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ContactDetailsCard
          contact={contact}
          isEditing={isEditing}
          formData={formData}
          setFormData={setFormData}
        />
        {stats && (
          <FinancialHealthCard stats={stats} />
        )}
      </div>

      {/* Row 2: Matters + Contracts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ClientMattersWidget clientId={contact.id} clientName={contact.name} />
        <ContractsSection clientId={contact.id} />
      </div>

      {/* Row 3: Activity Timeline — full width */}
      <ActivityTimeline clientId={contact.id} />
    </div>
  );
};

export default OverviewTab;
